import React from 'react';
import {
  Paper,
  Typography,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Alert,
  Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DescriptionIcon from '@mui/icons-material/Description';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

interface RelationshipPreviewProps {
  preview: any;
}

const RelationshipPreview: React.FC<RelationshipPreviewProps> = ({ preview }) => {
  const totalRelationships =
    (preview.ownershipsToMove || 0) + (preview.documentsToMove || 0);

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        Relationships to Merge
      </Typography>
      
      <Stack spacing={2} sx={{ mt: 2 }}>
        {/* Summary */}
        <Box
          sx={{
            p: 2,
            bgcolor: 'primary.50',
            borderRadius: 1,
            border: 1,
            borderColor: 'primary.main',
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="h6" color="primary.main">
              {totalRelationships}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              total relationship{totalRelationships !== 1 ? 's' : ''} will be moved to target person
            </Typography>
          </Stack>
        </Box>

        {/* Directorships/Shareholdings */}
        {preview.ownershipsToMove > 0 && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <AccountBalanceIcon color="primary" />
                <Typography variant="body1" fontWeight="medium">
                  Shareholdings & Directorships
                </Typography>
                <Chip
                  label={preview.ownershipsToMove}
                  size="small"
                  color="primary"
                />
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary">
                {preview.ownershipsToMove} ownership relationship(s) will be reassigned to the target person.
              </Typography>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Documents */}
        {preview.documentsToMove > 0 && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <DescriptionIcon color="primary" />
                <Typography variant="body1" fontWeight="medium">
                  Documents
                </Typography>
                <Chip
                  label={preview.documentsToMove}
                  size="small"
                  color="primary"
                />
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary">
                {preview.documentsToMove} document(s) will be reassigned to the target person.
              </Typography>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Ownership Collisions */}
        {preview.potentialOwnershipCollisions &&
          preview.potentialOwnershipCollisions.length > 0 && (
            <Alert severity="warning">
              <Typography variant="subtitle2" gutterBottom>
                Ownership Collisions Detected
              </Typography>
              <Stack spacing={0.5}>
                {preview.potentialOwnershipCollisions.map(
                  (collision: any, idx: number) => (
                    <Typography key={idx} variant="body2">
                      • Company {collision.ownedCompanyId}:{' '}
                      {collision.existingForTarget} existing +{' '}
                      {collision.countFromSources} from source
                      {collision.existingForTarget + collision.countFromSources > 1 &&
                        ' (may result in duplicate ownership)'}
                    </Typography>
                  )
                )}
              </Stack>
            </Alert>
          )}

        {/* No Relationships */}
        {totalRelationships === 0 && (
          <Alert severity="info">
            <Typography variant="body2">
              No relationships to move. The merge will only combine person data.
            </Typography>
          </Alert>
        )}
      </Stack>
    </Paper>
  );
};

export default RelationshipPreview;
